/* Telling a shake from ordinary handling, from the phone's motion sensor (about 60 readings a second).
   A shake is the phone being pushed one way and then back, quickly and firmly: 3 pushes (2 changes of direction)
   within 1 second, each above 10 m/s² once gravity is taken out. One quick out-and-back movement already gives those
   3 pushes (speed up, stop and come back, stop), so this is not strict about how long one shakes; a normal shake gives
   11-25 m/s²; walking or picking the phone up stays well under 10, and a single push-and-stop has only 2 pushes. After a shake it stays quiet for 1.5 seconds so one shake counts once.
   Gravity: the sensor's "acceleration" already has it taken out (iPhone gives it); otherwise "accelerationIncludingGravity"
   is used minus a slowly-following estimate of gravity. Pure code (no browser), so it can be tested on its own. */
export interface Vec { x: number; y: number; z: number }
export interface ShakeOpts { threshold: number; peaks: number; windowMs: number; quietMs: number }
export const SHAKE_DEFAULTS: ShakeOpts = {threshold: 10, peaks: 3, windowMs: 1000, quietMs: 1500};

/** feed(reading, time in ms) -> true when this reading completes a shake; seen(strength) gets every reading's strength
    in m/s² (gravity taken out), for the Shake test in Settings */
export function shakeDetector(o: ShakeOpts = SHAKE_DEFAULTS, seen?: (strength: number) => void) {
  let g: Vec | null = null, quietUntil = -Infinity;
  let peaks: {t: number; u: Vec}[] = [];
  return function feed(r: {linear?: Vec | null; withGravity?: Vec | null}, t: number): boolean {
    let l: Vec;
    if (r.linear) l = r.linear;
    else if (r.withGravity) {
      const a = r.withGravity;
      if (!g) g = {...a};
      l = {x: a.x - g.x, y: a.y - g.y, z: a.z - g.z};
      const k = 0.1;                                            // gravity follows slowly (a shake is too quick to move it much)
      g = {x: g.x + k * (a.x - g.x), y: g.y + k * (a.y - g.y), z: g.z + k * (a.z - g.z)};
    } else return false;
    const mag = Math.hypot(l.x, l.y, l.z);
    seen?.(mag);
    if (t < quietUntil) return false;
    peaks = peaks.filter(p => t - p.t <= o.windowMs);
    if (mag < o.threshold) return false;
    const u = {x: l.x / mag, y: l.y / mag, z: l.z / mag}, last = peaks[peaks.length - 1];
    if (last && u.x * last.u.x + u.y * last.u.y + u.z * last.u.z > -0.3) return false;   // still the same push
    peaks.push({t, u});
    if (peaks.length < o.peaks) return false;
    peaks = []; quietUntil = t + o.quietMs;
    return true;
  };
}
